import CloseIcon from '@mui/icons-material/Close';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Slide from '@mui/material/Slide';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import React, { useCallback, useMemo } from 'react';
import { TextField } from '@mui/material';
import { useColors } from 'legacyfrontend/hooks/useColors';

const Transition = React.forwardRef((props, ref) => {
  return <Slide direction='up' ref={ref} {...props} />;
});

// Estilos constantes para reutilización
const styles = {
  countryHeader: {
    padding: '8px 16px',
    fontWeight: 500,
    color: '#666',
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #e0e0e0',
  },
  listItem: {
    cursor: 'pointer',
    padding: '12px 16px',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  selectedItem: {
    backgroundColor: '#f5f5f5',
  },
  dialogContent: {
    overflowY: 'scroll',
    maxHeight: '90vh',
  },
};

// Agregar estilos CSS para la animación del spinner
const spinnerStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Insertar estilos en el head del documento
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = spinnerStyles;
  document.head.appendChild(styleElement);
}

export default function OrigenDestinoFullScreenDialog({
  options = [],
  onSelect,
  originSelected,
  disabled,
  isDestination,
  isDestinationEmpty,
  isOriginEmpty,
  setIsOriginEmpty,
  setIsDestinationEmpty,
  isOpen,
  setIsDestinationDialogOpen,
  setIsFullScreenRangeDatePickerOpen,
  setSearchValue,
  isFlight,
  handleAirportChange,
  isLoading = false,
  autoOpen = true,
}) {
  const [open, setOpen] = React.useState(false);
  const [optionTapped, setOptionTapped] = React.useState(undefined);
  const [localSearchValue, setLocalSearchValue] = React.useState('');

  const { background } = useColors();

  // Memoizar las opciones filtradas para evitar recálculos innecesarios
  const filteredOptions = useMemo(() => {
    if (!localSearchValue.trim()) return options;

    const searchLower = localSearchValue.toLowerCase();
    return options.filter((option) => {
      if (isFlight) {
        return (
          option.cityName?.toLowerCase().includes(searchLower) ||
          option.airportName?.toLowerCase().includes(searchLower) ||
          option.airportCode?.toLowerCase().includes(searchLower) ||
          option.countryName?.toLowerCase().includes(searchLower)
        );
      } else {
        return option.name?.toLowerCase().includes(searchLower);
      }
    });
  }, [options, localSearchValue, isFlight]);

  // Memoizar las opciones agrupadas para Flights
  const groupedOptions = useMemo(() => {
    if (!isFlight) return null;

    return filteredOptions.reduce((groups, option) => {
      const country = option.countryName || 'Otros';
      if (!groups[country]) {
        groups[country] = [];
      }
      groups[country].push(option);
      return groups;
    }, {});
  }, [filteredOptions, isFlight]);

  // Callbacks optimizados
  const handleClickOpen = useCallback(() => {
    if (!autoOpen) return; // No abrir automáticamente si autoOpen es false
    setOpen(true);
    setOptionTapped(originSelected);
    setLocalSearchValue('');
  }, [originSelected, autoOpen]);

  const handleManualOpen = useCallback(() => {
    setOpen(true);
    setOptionTapped(originSelected);
    setLocalSearchValue('');
  }, [originSelected]);

  const handleClose = useCallback(() => {
    setOptionTapped(undefined);
    setOpen(false);
    setLocalSearchValue('');
  }, []);

  const handleNext = useCallback(() => {
    if (!optionTapped) return;

    onSelect(optionTapped);
    setOpen(false);

    if (isDestination) {
      setIsDestinationEmpty(false);
      setIsDestinationDialogOpen(false);
      setIsFullScreenRangeDatePickerOpen(true);
    } else {
      setIsOriginEmpty(false);
      setIsDestinationDialogOpen(true);
    }
  }, [
    optionTapped,
    onSelect,
    isDestination,
    setIsDestinationEmpty,
    setIsDestinationDialogOpen,
    setIsFullScreenRangeDatePickerOpen,
    setIsOriginEmpty,
  ]);

  const handleSearch = useCallback(
    (event) => {
      const searchValue = event.target.value;

      if (setSearchValue) {
        setSearchValue(searchValue);
      }

      if (typeof handleAirportChange === 'function') {
        handleAirportChange(null, searchValue, !isDestination);
      }
    },
    [setSearchValue, handleAirportChange, isDestination]
  );

  const handleOptionSelect = useCallback((option) => {
    setOptionTapped(option);
  }, []);

  const handleInputChange = useCallback(
    (event) => {
      setLocalSearchValue(event.target.value);
      handleSearch(event);
    },
    [handleSearch]
  );

  // Efectos simplificados
  React.useEffect(() => {
    if (isOpen) {
      setOpen(true);
      setLocalSearchValue('');
    }
  }, [isOpen]);

  React.useEffect(() => {
    setOptionTapped(originSelected);
  }, [originSelected]);

  const renderOption = useCallback(
    (option, index) => {
      return (
        <ListItem
          key={option.id || index}
          onClick={() => handleOptionSelect(option)}
          sx={{
            ...styles.listItem,
            ...(optionTapped && option.id === optionTapped.id && styles.selectedItem),
          }}
        >
          <ListItemText
            primary={
              isFlight
                ? `${option.cityName} - ${option.airportName || option.cityName} (${option.airportCode || option.cityCode})`
                : option.name
            }
          />
        </ListItem>
      );
    },
    [handleOptionSelect, optionTapped, isFlight]
  );

  const renderGroupedOptions = useCallback(() => {
    if (!groupedOptions) return null;

    return Object.entries(groupedOptions).map(([country, countryOptions]) => (
      <div key={country}>
        <div style={styles.countryHeader}>{country}</div>
        <List>{countryOptions.map(renderOption)}</List>
      </div>
    ));
  }, [groupedOptions, renderOption]);

  const renderSimpleOptions = useCallback(
    () => <List>{filteredOptions.map(renderOption)}</List>,
    [filteredOptions, renderOption]
  );

  // Renderizar contenido principal
  const renderContent = useCallback(() => {
    if (isLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Typography color='textSecondary' sx={{ mb: 2 }}>
            {isFlight
              ? isDestination
                ? 'Buscando aeropuertos de destino...'
                : 'Buscando aeropuertos de origen...'
              : isDestination
                ? 'Buscando destinos...'
                : 'Buscando orígenes...'}
          </Typography>
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '3px solid #f3f3f3',
              borderTop: '3px solid #3498db',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto',
            }}
          />
        </div>
      );
    }

    if (!options.length) {
      return (
        <Typography color='textSecondary' sx={{ mt: 2, textAlign: 'center', padding: '2rem' }}>
          {isDestination
            ? isFlight
              ? 'Ingrese el destino'
              : 'Ingrese el destino'
            : isFlight
              ? 'Ingrese el origen'
              : 'Ingrese el origen'}
        </Typography>
      );
    }

    if (filteredOptions.length === 0 && localSearchValue.trim() !== '') {
      return (
        <Typography color='textSecondary' sx={{ mt: 2 }}>
          Sin resultados
        </Typography>
      );
    }

    if (filteredOptions.length === 0) {
      return (
        <Typography color='textSecondary' sx={{ mt: 2, textAlign: 'center', padding: '2rem' }}>
          {isDestination
            ? isFlight
              ? 'Escriba para buscar aeropuertos de destino'
              : 'Escriba para buscar destinos'
            : isFlight
              ? 'Escriba para buscar aeropuertos de origen'
              : 'Escriba para buscar orígenes'}
        </Typography>
      );
    }

    return isFlight ? renderGroupedOptions() : renderSimpleOptions();
  }, [
    isLoading,
    options.length,
    filteredOptions.length,
    localSearchValue,
    isFlight,
    renderGroupedOptions,
    renderSimpleOptions,
  ]);

  // Memoizar el texto del botón
  const displayText = useMemo(() => {
    if (isFlight && originSelected) {
      return `${originSelected.cityName} - ${originSelected.airportName} (${originSelected.airportCode})`;
    }
    return originSelected?.name || ' ';
  }, [isFlight, originSelected]);

  return (
    <>
      <div
        className={`full-screen-dialog ${
          isDestinationEmpty || isOriginEmpty ? 'autocomplete-error' : ''
        }`}
        onClick={() => {
          if (!disabled) {
            if (autoOpen) {
              handleClickOpen();
            } else {
              handleManualOpen();
            }
          }
        }}
      >
        {displayText}
      </div>

      <Dialog
        TransitionComponent={Transition}
        disableEnforceFocus
        disableScrollLock={false}
        fullScreen
        onClose={handleClose}
        open={open}
        slotProps={{
          backdrop: {
            style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
          },
        }}
      >
        <AppBar sx={{ position: 'relative', backgroundColor: background }}>
          <Toolbar>
            <IconButton aria-label='close' color='inherit' edge='start' onClick={handleClose}>
              <CloseIcon />
            </IconButton>
            <Typography component='div' sx={{ ml: 2, flex: 1 }} variant='h6'>
              {isDestination ? 'Destino' : 'Origen'}
            </Typography>
            {optionTapped && (
              <Button autoFocus color='inherit' onClick={handleNext}>
                Siguiente
              </Button>
            )}
          </Toolbar>
        </AppBar>
        <DialogTitle>Escriba para buscar una opción</DialogTitle>

        <DialogContent sx={styles.dialogContent}>
          <TextField
            autoFocus
            fullWidth
            inputProps={{
              autoComplete: 'off',
              autoCorrect: 'off',
              autoCapitalize: 'off',
              spellCheck: 'false',
            }}
            onChange={handleInputChange}
            placeholder='Buscar'
            sx={{ marginBottom: '1rem' }}
            value={localSearchValue}
          />
          {renderContent()}
        </DialogContent>
      </Dialog>
    </>
  );
}
